import Api from '@v2/shared/lib/searchSuggestions';
import { useSite } from '@v2/shared/lib/site';
import useAutoComplete, { SPACER, Item } from '@v2/shared/lib/useAutoComplete';
import { ChangeEvent, KeyboardEvent, Ref } from 'react';

export type SearchAutoCompleteProps = {
  query: string;
  clearQuery: () => void;
  open: boolean;
  selectedItemIndex: number;
  isSelected: (index: number) => boolean;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur: () => void;
  onFocus: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
  onClick: (Item?: Item) => void;
  inputProps: any;
  inputRef: Ref<HTMLInputElement>;
  items: Item[];
  itemsContainerProps: { ref: (ref: any) => void };
  isSpacer: (index: number) => boolean;
};

export type SearchCallback = (item: Item) => void;

// the suggestions api can return the same title more than once, both within a list (several
// products sharing an author) and across the two lists (a title that is also an author)
const dedupeByTitle = <T extends { title: string }>(suggestions: T[], seen: Set<string>): T[] => {
  return suggestions.filter(({ title }) => {
    const key = title?.trim().toLowerCase() ?? '';

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);

    return true;
  });
};

const useSearchAutoComplete = (callback: SearchCallback): SearchAutoCompleteProps => {
  const site = useSite();

  const {
    query,
    clearQuery,
    open,
    items,
    selectedItemIndex,
    isSelected,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    onClick,
    inputProps,
    inputRef,
    itemsContainerProps,
    isSpacer,
  } = useAutoComplete(async (query) => {
    let [titles, authors] = await Promise.all([
      Api.search.suggestions({
        siteId: site.id,
        searchTerm: query,
        searchFields: ['title', 'product_identifiers'],
        returnField: 'title',
      }),
      Api.search.suggestions({
        siteId: site.id,
        searchTerm: query,
        searchFields: ['authors'],
        returnField: 'authors',
      }),
    ]);

    const seen = new Set<string>();
    titles = dedupeByTitle(titles, seen);

    // limit 3 authors and 10 total results
    authors = dedupeByTitle(authors, seen).slice(0, 3);
    titles = titles.slice(0, 10 - authors.length);

    const results = titles;

    if (results.length > 0 && authors.length > 0) {
      results.push({ title: SPACER });
    }

    return [...results, ...authors];
  }, callback);

  return {
    query,
    clearQuery,
    open,
    items,
    selectedItemIndex,
    isSelected,
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    onClick,
    inputProps,
    inputRef,
    itemsContainerProps,
    isSpacer,
  };
};

export default useSearchAutoComplete;
